import { Injectable } from '@angular/core';
import { OptionPrice, ProductConfigurationOptionPriceService } from './product-configuration-option-price.service';
import { PriceService } from '../price/price.service';
import { forkJoin, Observable, of, switchMap, first } from 'rxjs';
import { CartRowFeatureOptionSelection, ProductConfiguration } from '../../models/product-configuration-types';
import { CustomerProductPrice } from '../../models/price';
import { Product } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class ProductConfigurationPriceTotalService {
  constructor(
    protected optionPriceService: ProductConfigurationOptionPriceService,
    protected priceService: PriceService
  ) {}

  getConfigurationTotalPrice(
    configuration: ProductConfiguration,
    selections: CartRowFeatureOptionSelection[],
    product?: Product
  ): Observable<CpqPrice> {
    const priceQuestions = [];

    for (const selection of selections) {
      priceQuestions.push(this.optionPriceService.getPrice(selection.option).pipe(first()));
    }

    return forkJoin([
      this.priceService
        .getCurrentCustomerPrice(product?.extra?.pim?.configurationTemplate || configuration.id)
        .pipe(first()),
      ...priceQuestions
    ]).pipe(
      switchMap(prices => {
        let configurationPrice: CustomerProductPrice = prices.splice(0, 1)[0];

        const basePrice = this.getConfigurationBasePrice(configurationPrice);

        let total = prices.reduce((sum, price) => (sum += price.price), 0);
        total += basePrice;

        const priceMap = new Map<string, OptionPrice>();
        prices.forEach(price => {
          priceMap.set(price.id, price);
        });
        return of({
          basePrice,
          totalPrice: total,
          currency: prices[0]?.currency,
          discountPercentage: configurationPrice.customerDiscountPercent || 0,
          optionPrices: priceMap
        });
      })
    );
  }

  /**
   * What the configured product itself contributes to the total. Gross by default, so it lines up
   * with the option prices, which are the pricelist sum unless the shop overrides them; a shop that
   * quotes net overrides both together.
   */
  protected getConfigurationBasePrice(configurationPrice: CustomerProductPrice): number {
    return configurationPrice.customerGrossPrice.value;
  }
}

export interface CpqPrice {
  basePrice: number;
  totalPrice: number;
  currency: string;
  discountPercentage: number;
  optionPrices: Map<string, OptionPrice>;
}
